import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useDatasets } from "../context/DatasetContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Siren,
  AlertTriangle,
  AlertCircle,
  Info,
  DollarSign,
  User,
  Hash,
  ChevronRight,
  Filter,
  X,
  ExternalLink,
  Search,
  CheckCircle2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const anomalyTypeLabels = {
  large_donation: "Large Donation",
  high_frequency: "High Frequency Donor",
  repeated_amount: "Repeated Amount",
  micro_donations: "Micro Donations",
};

const anomalyTypeIcons = {
  large_donation: DollarSign,
  high_frequency: User,
  repeated_amount: Hash,
  micro_donations: AlertCircle,
};

const CATEGORIES = [
  { key: "investigate", label: "Investigate", color: "bg-red-100 text-red-700 border-red-300" },
  { key: "research", label: "Research", color: "bg-blue-100 text-blue-700 border-blue-300" },
  { key: "all_good", label: "All Good", color: "bg-green-100 text-green-700 border-green-300" },
];

export default function AnomaliesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { debouncedSelectedIds } = useDatasets();
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnomaly, setSelectedAnomaly] = useState(null);
  // Filter state is initialised from the URL so that navigating away (e.g.
  // to /data via "View Related Donations") and coming back preserves the
  // user's selections. All updates go through `setFilterParam` which mirrors
  // the value into the URL. Defaults are omitted from the URL to keep it
  // clean for the common empty-filter case.
  const [severityFilter, setSeverityFilter] = useState(searchParams.get("severity") || "all");
  const [typeFilter, setTypeFilter] = useState(searchParams.get("type") || "all");
  const [minAmount, setMinAmount] = useState(searchParams.get("minAmount") || "");
  const [maxAmount, setMaxAmount] = useState(searchParams.get("maxAmount") || "");
  const [minDonations, setMinDonations] = useState(searchParams.get("minDonations") || "");
  const [maxDonations, setMaxDonations] = useState(searchParams.get("maxDonations") || "");
  const [showFilters, setShowFilters] = useState(searchParams.get("panel") === "1");
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get("category") || "all");

  // Helper: sync a single filter value into the URL, dropping keys that are
  // at their default value so the querystring stays tidy.
  const syncParam = (key, value, isDefault) => {
    const next = new URLSearchParams(searchParams);
    if (isDefault) { next.delete(key); } else { next.set(key, value); }
    setSearchParams(next, { replace: true });
  };

  const handleSetCategory = async (anomalyId, category) => {
    // If already this category, clear it
    const anomaly = anomalies.find(a => a.id === anomalyId);
    const newCategory = anomaly?.category === category ? null : category;
    try {
      await axios.put(`${API}/anomalies/${anomalyId}/category`, { category: newCategory });
      setAnomalies(prev => prev.map(a =>
        a.id === anomalyId ? { ...a, category: newCategory || undefined } : a
      ));
      if (selectedAnomaly?.id === anomalyId) {
        setSelectedAnomaly(prev => ({ ...prev, category: newCategory || undefined }));
      }
    } catch (error) {
      console.error("Failed to set category:", error);
    }
  };

  const handleViewRelatedDonations = () => {
    if (selectedAnomaly?.contributor_name) {
      navigate(`/data?donor=${encodeURIComponent(selectedAnomaly.contributor_name)}`);
    }
  };

  useEffect(() => {
    fetchAnomalies();
  }, [severityFilter, typeFilter, debouncedSelectedIds]);

  // URL-syncing wrappers around the raw setters. Downstream JSX uses these
  // so a single call updates both React state and the address bar.
  const updateSeverityFilter = (v) => { setSeverityFilter(v); syncParam("severity", v, v === "all"); };
  const updateTypeFilter = (v) => { setTypeFilter(v); syncParam("type", v, v === "all"); };
  const updateCategoryFilter = (v) => { setCategoryFilter(v); syncParam("category", v, v === "all"); };
  const updateMinAmount = (v) => { setMinAmount(v); syncParam("minAmount", v, !v); };
  const updateMaxAmount = (v) => { setMaxAmount(v); syncParam("maxAmount", v, !v); };
  const updateMinDonations = (v) => { setMinDonations(v); syncParam("minDonations", v, !v); };
  const updateMaxDonations = (v) => { setMaxDonations(v); syncParam("maxDonations", v, !v); };
  const updateShowFilters = (v) => { setShowFilters(v); syncParam("panel", "1", !v); };

  const fetchAnomalies = async () => {
    if (!debouncedSelectedIds) {
      setAnomalies([]);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (severityFilter !== "all") params.append("severity", severityFilter);
      if (typeFilter !== "all") params.append("anomaly_type", typeFilter);
      params.append("dataset_ids", debouncedSelectedIds);

      const response = await axios.get(`${API}/anomalies?${params.toString()}`);
      setAnomalies(response.data);
    } catch (error) {
      console.error("Failed to fetch anomalies:", error);
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering for amount and donation count
  const filteredAnomalies = useMemo(() => {
    return anomalies.filter(anomaly => {
      if (minAmount && anomaly.total_amount !== null && anomaly.total_amount < parseFloat(minAmount)) return false;
      if (maxAmount && anomaly.total_amount !== null && anomaly.total_amount > parseFloat(maxAmount)) return false;
      if (minDonations && anomaly.donation_count !== null && anomaly.donation_count < parseInt(minDonations)) return false;
      if (maxDonations && anomaly.donation_count !== null && anomaly.donation_count > parseInt(maxDonations)) return false;
      if (categoryFilter === "uncategorized" && anomaly.category) return false;
      if (categoryFilter !== "all" && categoryFilter !== "uncategorized" && anomaly.category !== categoryFilter) return false;
      return true;
    });
  }, [anomalies, minAmount, maxAmount, minDonations, maxDonations, categoryFilter]);

  const hasActiveFilters = minAmount || maxAmount || minDonations || maxDonations || severityFilter !== "all" || typeFilter !== "all" || categoryFilter !== "all";

  const clearAllFilters = () => {
    setSeverityFilter("all");
    setTypeFilter("all");
    setCategoryFilter("all");
    setMinAmount("");
    setMaxAmount("");
    setMinDonations("");
    setMaxDonations("");
    // Clear all persisted filter keys from the URL, but keep the panel-open
    // preference so the empty filter form stays visible.
    const next = new URLSearchParams(searchParams);
    ["severity", "type", "category", "minAmount", "maxAmount", "minDonations", "maxDonations"].forEach(k => next.delete(k));
    setSearchParams(next, { replace: true });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value);
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case "high":
        return <AlertTriangle className="w-4 h-4" />;
      case "medium":
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Info className="w-4 h-4" />;
    }
  };

  const getSeverityBadgeClass = (severity) => {
    switch (severity) {
      case "high":
        return "bg-red-100 text-red-700 border-red-200 hover:bg-red-100";
      case "medium":
        return "bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100";
      default:
        return "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-100";
    }
  };

  const groupedAnomalies = {
    high: filteredAnomalies.filter((a) => a.severity === "high"),
    medium: filteredAnomalies.filter((a) => a.severity === "medium"),
    low: filteredAnomalies.filter((a) => a.severity === "low"),
  };

  return (
    <div className="p-4 md:p-8 space-y-4 md:space-y-6" data-testid="anomalies-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-3">
            <Siren className="w-8 h-8 text-red-500" />
            Anomaly Detection
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Flagged transactions requiring review
            {filteredAnomalies.length !== anomalies.length && (
              <span className="ml-2">
                (showing {filteredAnomalies.length} of {anomalies.length})
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant={showFilters ? "default" : "outline"}
            size="sm"
            onClick={() => updateShowFilters(!showFilters)}
            className="rounded-sm"
            data-testid="toggle-anomaly-filters"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            {hasActiveFilters && (
              <span className="ml-2 w-2 h-2 rounded-full bg-accent" />
            )}
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="border rounded-sm shadow-sm animate-fade-in" data-testid="anomaly-filters-panel">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                  Severity
                </Label>
                <Select value={severityFilter} onValueChange={updateSeverityFilter}>
                  <SelectTrigger className="rounded-sm" data-testid="severity-filter">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Severities</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                  Type
                </Label>
                <Select value={typeFilter} onValueChange={updateTypeFilter}>
                  <SelectTrigger className="rounded-sm" data-testid="type-filter">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="large_donation">Large Donation</SelectItem>
                    <SelectItem value="high_frequency">High Frequency</SelectItem>
                    <SelectItem value="repeated_amount">Repeated Amount</SelectItem>
                    <SelectItem value="micro_donations">Micro Donations</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                  Min Amount
                </Label>
                <Input
                  type="number"
                  placeholder="$0"
                  value={minAmount}
                  onChange={(e) => updateMinAmount(e.target.value)}
                  className="rounded-sm"
                  data-testid="min-amount-filter"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                  Max Amount
                </Label>
                <Input
                  type="number"
                  placeholder="No limit"
                  value={maxAmount}
                  onChange={(e) => updateMaxAmount(e.target.value)}
                  className="rounded-sm"
                  data-testid="max-amount-filter"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                  Min Donations
                </Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={minDonations}
                  onChange={(e) => updateMinDonations(e.target.value)}
                  className="rounded-sm"
                  data-testid="min-donations-filter"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                  Max Donations
                </Label>
                <Input
                  type="number"
                  placeholder="No limit"
                  value={maxDonations}
                  onChange={(e) => updateMaxDonations(e.target.value)}
                  className="rounded-sm"
                  data-testid="max-donations-filter"
                />
              </div>
            </div>
            {hasActiveFilters && (
              <div className="mt-4 pt-4 border-t border-border flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="text-muted-foreground"
                  data-testid="clear-anomaly-filters"
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear all filters
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Category Filter Tabs */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="category-filter-tabs">
        <span className="text-xs text-muted-foreground font-medium mr-1">Category:</span>
        <button
          onClick={() => updateCategoryFilter("all")}
          className={`px-3 py-1 text-xs rounded-full border transition-colors ${categoryFilter === "all" ? "bg-foreground text-background border-foreground" : "bg-background text-foreground border-border hover:bg-muted"}`}
        >All</button>
        <button
          onClick={() => updateCategoryFilter("uncategorized")}
          className={`px-3 py-1 text-xs rounded-full border transition-colors ${categoryFilter === "uncategorized" ? "bg-foreground text-background border-foreground" : "bg-background text-foreground border-border hover:bg-muted"}`}
        >Uncategorized</button>
        {CATEGORIES.map(cat => (
          <button
            key={cat.key}
            onClick={() => updateCategoryFilter(categoryFilter === cat.key ? "all" : cat.key)}
            className={`px-3 py-1 text-xs rounded-full border transition-colors ${categoryFilter === cat.key ? cat.color + " font-medium" : "bg-background text-foreground border-border hover:bg-muted"}`}
          >{cat.label} ({anomalies.filter(a => a.category === cat.key).length})</button>
        ))}
      </div>

      {/* Stats Summary - Clickable Filters */}
      <div className="grid grid-cols-3 gap-4">
        <Card 
          className={`border rounded-sm shadow-sm cursor-pointer transition-all duration-150 hover:scale-[1.02] ${
            severityFilter === 'high' 
              ? 'bg-red-100 border-red-400 ring-2 ring-red-400' 
              : 'bg-red-50/50 border-red-200 hover:bg-red-100/50'
          }`}
          onClick={() => updateSeverityFilter(severityFilter === 'high' ? 'all' : 'high')}
          data-testid="high-severity-count"
        >
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-red-600 font-medium">
                High Severity
              </p>
              <p className="font-mono text-2xl font-semibold text-red-700 mt-1">
                {groupedAnomalies.high.length}
              </p>
            </div>
            <AlertTriangle className={`w-8 h-8 ${severityFilter === 'high' ? 'text-red-600' : 'text-red-400'}`} />
          </CardContent>
        </Card>
        <Card 
          className={`border rounded-sm shadow-sm cursor-pointer transition-all duration-150 hover:scale-[1.02] ${
            severityFilter === 'medium' 
              ? 'bg-amber-100 border-amber-400 ring-2 ring-amber-400' 
              : 'bg-amber-50/50 border-amber-200 hover:bg-amber-100/50'
          }`}
          onClick={() => updateSeverityFilter(severityFilter === 'medium' ? 'all' : 'medium')}
          data-testid="medium-severity-count"
        >
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-amber-600 font-medium">
                Medium Severity
              </p>
              <p className="font-mono text-2xl font-semibold text-amber-700 mt-1">
                {groupedAnomalies.medium.length}
              </p>
            </div>
            <AlertCircle className={`w-8 h-8 ${severityFilter === 'medium' ? 'text-amber-600' : 'text-amber-400'}`} />
          </CardContent>
        </Card>
        <Card 
          className={`border rounded-sm shadow-sm cursor-pointer transition-all duration-150 hover:scale-[1.02] ${
            severityFilter === 'low' 
              ? 'bg-slate-200 border-slate-400 ring-2 ring-slate-400' 
              : 'bg-slate-50/50 border-slate-200 hover:bg-slate-100/50'
          }`}
          onClick={() => updateSeverityFilter(severityFilter === 'low' ? 'all' : 'low')}
          data-testid="low-severity-count"
        >
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-600 font-medium">
                Low Severity
              </p>
              <p className="font-mono text-2xl font-semibold text-slate-700 mt-1">
                {groupedAnomalies.low.length}
              </p>
            </div>
            <Info className={`w-8 h-8 ${severityFilter === 'low' ? 'text-slate-600' : 'text-slate-400'}`} />
          </CardContent>
        </Card>
      </div>

      {/* Main Content - Split View */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Anomaly List */}
        <Card className="border rounded-sm shadow-sm" data-testid="anomaly-list">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg font-medium">
              Flagged Items ({filteredAnomalies.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4 space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-20 bg-muted animate-pulse rounded-sm" />
                ))}
              </div>
            ) : filteredAnomalies.length === 0 ? (
              <div className="p-8 text-center">
                <Siren className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">
                  {anomalies.length === 0 ? "No anomalies detected" : "No anomalies match your filters"}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {anomalies.length === 0 ? "Upload data to start analysis" : "Try adjusting your filter criteria"}
                </p>
              </div>
            ) : (
              <div className="max-h-[600px] overflow-y-auto">
                {filteredAnomalies.map((anomaly, index) => {
                  const TypeIcon = anomalyTypeIcons[anomaly.anomaly_type] || AlertCircle;
                  return (
                    <div
                      key={anomaly.id}
                      onClick={() => setSelectedAnomaly(anomaly)}
                      className={`p-4 border-b border-border/50 cursor-pointer transition-all duration-75 hover:bg-muted/50 animate-fade-in ${
                        selectedAnomaly?.id === anomaly.id
                          ? "bg-muted/50 border-l-2 border-l-accent"
                          : ""
                      }`}
                      style={{ animationDelay: `${index * 0.02}s` }}
                      data-testid={`anomaly-item-${index}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-sm ${getSeverityBadgeClass(anomaly.severity)}`}>
                          <TypeIcon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge
                              variant="outline"
                              className={`text-[10px] ${getSeverityBadgeClass(anomaly.severity)}`}
                            >
                              {anomaly.severity.toUpperCase()}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {anomalyTypeLabels[anomaly.anomaly_type] || anomaly.anomaly_type}
                            </span>
                          </div>
                          <p className="text-sm line-clamp-2">{anomaly.description}</p>
                          <div className="flex items-center gap-1 mt-1.5">
                            {anomaly.contributor_name && (
                              <span className="text-xs text-muted-foreground truncate max-w-[120px]">
                                {anomaly.contributor_name}
                              </span>
                            )}
                            <div className="flex items-center gap-1 ml-auto">
                              {CATEGORIES.map(cat => (
                                <button
                                  key={cat.key}
                                  onClick={(e) => { e.stopPropagation(); handleSetCategory(anomaly.id, cat.key); }}
                                  className={`px-1.5 py-0.5 text-[10px] rounded border transition-all ${
                                    anomaly.category === cat.key
                                      ? cat.color + " font-medium"
                                      : "border-transparent text-muted-foreground hover:border-border hover:bg-muted"
                                  }`}
                                  data-testid={`category-btn-${cat.key}-${index}`}
                                >{cat.label}</button>
                              ))}
                            </div>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Anomaly Details */}
        <Card className="border rounded-sm shadow-sm" data-testid="anomaly-details">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg font-medium">Details</CardTitle>
          </CardHeader>
          <CardContent>
            {selectedAnomaly ? (
              <div className="space-y-6 animate-fade-in">
                {/* Header */}
                <div className="flex items-start gap-4">
                  <div
                    className={`p-3 rounded-sm ${getSeverityBadgeClass(selectedAnomaly.severity)}`}
                  >
                    {getSeverityIcon(selectedAnomaly.severity)}
                  </div>
                  <div>
                    <Badge
                      variant="outline"
                      className={getSeverityBadgeClass(selectedAnomaly.severity)}
                    >
                      {selectedAnomaly.severity.toUpperCase()} SEVERITY
                    </Badge>
                    <h3 className="font-heading text-xl font-medium mt-2">
                      {anomalyTypeLabels[selectedAnomaly.anomaly_type] ||
                        selectedAnomaly.anomaly_type}
                    </h3>
                  </div>
                </div>

                {/* Description */}
                <div className="p-4 bg-muted/50 rounded-sm border">
                  <p className="text-sm">{selectedAnomaly.description}</p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedAnomaly.contributor_name && (
                    <div className="p-4 border rounded-sm">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                        Contributor
                      </p>
                      <p className="text-sm font-medium truncate">
                        {selectedAnomaly.contributor_name}
                      </p>
                    </div>
                  )}
                  {selectedAnomaly.total_amount !== null && (
                    <div className="p-4 border rounded-sm">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                        Total Amount
                      </p>
                      <p className="font-mono text-lg font-semibold text-emerald-600">
                        {formatCurrency(selectedAnomaly.total_amount)}
                      </p>
                    </div>
                  )}
                  {selectedAnomaly.donation_count !== null && (
                    <div className="p-4 border rounded-sm">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                        Donation Count
                      </p>
                      <p className="font-mono text-lg font-semibold">
                        {selectedAnomaly.donation_count}
                      </p>
                    </div>
                  )}
                  <div className="p-4 border rounded-sm">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                      Related Records
                    </p>
                    <p className="font-mono text-lg font-semibold">
                      {selectedAnomaly.related_records?.length || 0}
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3 pt-4 border-t">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Category</p>
                    <div className="flex gap-2">
                      {CATEGORIES.map(cat => (
                        <Button
                          key={cat.key}
                          variant="outline"
                          size="sm"
                          className={`rounded-sm ${
                            selectedAnomaly.category === cat.key
                              ? cat.color + " font-medium border"
                              : ""
                          }`}
                          onClick={() => handleSetCategory(selectedAnomaly.id, cat.key)}
                          data-testid={`detail-category-btn-${cat.key}`}
                        >
                          {selectedAnomaly.category === cat.key && <CheckCircle2 className="w-3 h-3 mr-1.5" />}
                          {cat.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="rounded-sm w-full"
                    onClick={handleViewRelatedDonations}
                    disabled={!selectedAnomaly?.contributor_name}
                    data-testid="view-related-donations-btn"
                  >
                    <ExternalLink className="w-3 h-3 mr-2" />
                    View Related Donations
                  </Button>
                </div>
              </div>
            ) : (
              <div className="py-16 text-center">
                <AlertCircle className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">Select an anomaly to view details</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
